/* Kanban самбар — дизайны queue-board.jsx-ээс (posMap нь Guid key болсон) */
import { useState } from 'react';
import { QueueCard } from '@/components/QueueCard';
import { SearchBox } from '@/components/bits';
import { STATUS_META, STATUS_ORDER } from '@/shared/data';
import { matchesQuery, tug } from '@/shared/format';
import { IconDroplets, IconPlus } from '@/shared/icons';
import type { CarStatus, DayStats, QueueCar } from '@/shared/types';

export interface BoardHandlers {
  cars: QueueCar[];
  now: number;
  stats: DayStats;
  onAdvance: (car: QueueCar) => void;
  onCancel: (car: QueueCar) => void;
  posMap: Record<string, number>;
  query: string;
  setQuery: (q: string) => void;
  onIntake: () => void;
  currentWasher: string;
}

function Column({ status, cars, total, now, onAdvance, onCancel, posMap }: {
  status: CarStatus;
  cars: QueueCar[];
  total: number;
  now: number;
  onAdvance: (car: QueueCar) => void;
  onCancel: (car: QueueCar) => void;
  posMap: Record<string, number>;
}) {
  const meta = STATUS_META[status];
  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <div className="flex items-center gap-2 px-1 pb-2">
        <span className={'w-2 h-2 rounded-full ' + meta.dot}></span>
        <span className="text-[13px] font-bold text-slate-700">{meta.label}</span>
        <span className="text-xs font-bold text-slate-400 bg-slate-200/70 rounded-full px-2 py-0.5">
          {cars.length === total ? cars.length : cars.length + '/' + total}
        </span>
      </div>
      <div className="flex flex-col flex-1 gap-2 rounded-[calc(var(--r)+4px)] bg-slate-200/40 p-2">
        {cars.map((car) => (
          <QueueCard key={car.id} car={car} now={now} onAdvance={onAdvance} onCancel={onCancel}
            queuePos={status === 'waiting' ? posMap[car.id] : null} />
        ))}
        {cars.length === 0 && (
          <div className="flex-1 min-h-[100px] flex items-center justify-center text-[13px] text-slate-400">
            {total > 0 ? 'Хайлтад тохирох машин алга' : 'Хоосон'}
          </div>
        )}
      </div>
    </div>
  );
}

export function QueueContent({ cars, now, onAdvance, onCancel, posMap, query, setQuery }:
  Omit<BoardHandlers, 'stats' | 'onIntake' | 'currentWasher'>) {
  const sorted = [...cars].sort((a, b) => a.enteredAt - b.enteredAt);
  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-4">
      <SearchBox value={query} onChange={setQuery} className="w-[340px] mb-3" />
      <div className="flex gap-3 items-start">
        {STATUS_ORDER.map((s) => {
          const all = sorted.filter((c) => c.status === s);
          return (
            <Column key={s} status={s} total={all.length}
              cars={all.filter((c) => matchesQuery(c, query))}
              now={now} onAdvance={onAdvance} onCancel={onCancel} posMap={posMap} />
          );
        })}
      </div>
    </div>
  );
}

export function MobileBoard({ cars, now, stats, onAdvance, onCancel, posMap, query, setQuery, onIntake, currentWasher }: BoardHandlers) {
  const [tab, setTab] = useState<CarStatus>('waiting');
  const list = cars
    .filter((c) => c.status === tab && matchesQuery(c, query))
    .sort((a, b) =>
      (Number(b.washers.some((w) => w.name === currentWasher)) - Number(a.washers.some((w) => w.name === currentWasher)))
      || (a.enteredAt - b.enteredAt));
  return (
    <div className="h-full flex flex-col bg-slate-50">
      <header className="px-4 pt-4 pb-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-[10px] bg-gradient-to-br from-[var(--brand1)] to-[var(--brand2)] flex items-center justify-center text-white">
            <IconDroplets size={17} strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="font-extrabold text-slate-900 text-sm">Carwash</div>
            <div className="text-[11px] text-slate-400">Өнөөдөр: {stats.cars} машин · {tug(stats.revenue)}</div>
          </div>
          {currentWasher && (
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-800 bg-cyan-50 ring-1 ring-cyan-200 rounded-full pl-1 pr-2.5 py-1">
              <span className="w-5 h-5 rounded-full bg-[var(--brand2)] text-white text-[11px] font-bold flex items-center justify-center">{currentWasher[0]}</span>
              {currentWasher}
            </span>
          )}
        </div>
        <SearchBox value={query} onChange={setQuery} className="mt-3" />
        <div className="mt-2 grid grid-cols-3 gap-1 bg-slate-100 rounded-[12px] p-1">
          {STATUS_ORDER.map((s) => (
            <button key={s} onClick={() => setTab(s)} className={
              'min-h-[40px] rounded-[9px] text-[13px] font-semibold transition ' +
              (tab === s ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500')
            }>
              {STATUS_META[s].short}
              <span className="ml-1 text-[11px] font-bold text-slate-400">{cars.filter((c) => c.status === s).length}</span>
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 flex flex-col gap-3 pb-24">
        {list.map((car) => (
          <div key={car.id} className="relative">
            {!!currentWasher && car.washers.some((w) => w.name === currentWasher) && (
              <span className="absolute -top-2 left-3 z-10 text-[10px] font-bold text-white bg-[var(--brand2)] rounded-full px-2 py-0.5">Миний машин</span>
            )}
            <QueueCard car={car} now={now} onAdvance={onAdvance} onCancel={onCancel} big
              queuePos={tab === 'waiting' ? posMap[car.id] : null} currentWasher={currentWasher} />
          </div>
        ))}
        {list.length === 0 && (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-400 py-16">
            {query.trim() ? 'Хайлтад тохирох машин алга' : 'Хоосон'}
          </div>
        )}
      </main>

      <button onClick={onIntake} title="Машин хүлээн авах"
        className="absolute bottom-[84px] right-3 z-20 w-14 h-14 rounded-full text-white bg-gradient-to-br from-[var(--brand1)] to-[var(--brand2)] shadow-lg shadow-cyan-600/30 flex items-center justify-center active:scale-[0.95] transition">
        <IconPlus size={24} strokeWidth={2.5} />
      </button>
    </div>
  );
}
